import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Geführte „Erste Schritte"-Checkliste im Campus-Dashboard.
 * steps: [{ id, label, desc, done, to?, action? ('resend-email') }]
 */
export default function OnboardingChecklist({ user, steps }) {
  const [sending, setSending] = useState(false);
  const doneCount = steps.filter((s) => s.done).length;
  if (doneCount === steps.length) return null;

  const resendVerification = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: user.email });
      toast.success("Bestätigungs-Mail verschickt – bitte prüfe dein Postfach.");
    } catch {
      toast.error("Mail konnte nicht gesendet werden – bitte später erneut versuchen.");
    }
    setSending(false);
  };

  return (
    <section
      data-testid="dashboard-onboarding-checklist"
      className="rounded-2xl border border-terra/25 bg-white p-7 lg:p-8"
    >
      <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-terra mb-2">
            Deine ersten Schritte
          </div>
          <h2 className="font-display text-sand-800 text-[1.5rem] font-semibold leading-tight">
            So holst du alles aus dem Campus.
          </h2>
        </div>
        <div className="text-[13px] font-medium text-sand-600" data-testid="onboarding-progress-label">
          {doneCount} von {steps.length} erledigt
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 rounded-full bg-cream-200 overflow-hidden mb-6">
        <div
          className="h-full bg-terra rounded-full transition-all duration-500"
          style={{ width: `${(doneCount / steps.length) * 100}%` }}
        />
      </div>

      <ol className="space-y-1">
        {steps.map((s, i) => {
          const inner = (
            <>
              <span
                className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-bold ${
                  s.done
                    ? "bg-sage/25 text-sage-dark"
                    : "bg-cream-200 text-sand-600"
                }`}
              >
                {s.done ? <Check className="h-3.5 w-3.5" strokeWidth={2.6} /> : i + 1}
              </span>
              <span className="flex-1 min-w-0">
                <span className={`block text-[14.5px] font-medium ${s.done ? "text-sand-500 line-through decoration-sand-400" : "text-sand-800"}`}>
                  {s.label}
                </span>
                {!s.done && s.desc && (
                  <span className="block text-[12.5px] text-sand-600 mt-0.5 leading-snug">{s.desc}</span>
                )}
              </span>
              {!s.done && (
                <span className="shrink-0 inline-flex items-center gap-1.5 text-[11.5px] uppercase tracking-[0.18em] font-bold text-terra">
                  {s.action === "resend-email"
                    ? (sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Mail senden")
                    : s.cta || "Los geht's"}
                  {s.action !== "resend-email" && (
                    <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
                  )}
                </span>
              )}
            </>
          );

          const rowCls = `group flex items-center gap-4 rounded-xl px-3 py-3 -mx-3 transition-colors ${
            s.done ? "" : "hover:bg-cream-50"
          }`;

          if (s.done) {
            return (
              <li key={s.id} data-testid={`onboarding-step-${s.id}`} className={rowCls}>
                {inner}
              </li>
            );
          }
          if (s.action === "resend-email") {
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={resendVerification}
                  disabled={sending}
                  data-testid={`onboarding-step-${s.id}`}
                  className={`${rowCls} w-full text-left`}
                >
                  {inner}
                </button>
              </li>
            );
          }
          return (
            <li key={s.id}>
              <Link to={s.to} data-testid={`onboarding-step-${s.id}`} className={rowCls}>
                {inner}
              </Link>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
